import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export type SupportedLanguage =
	| 'ar'
	| 'bg'
	| 'bn'
	| 'cs'
	| 'da'
	| 'de'
	| 'el'
	| 'en'
	| 'es'
	| 'et'
	| 'fa'
	| 'fi'
	| 'fr'
	| 'ga'
	| 'hi'
	| 'hr'
	| 'hu'
	| 'is'
	| 'it'
	| 'lb'
	| 'lt'
	| 'lv'
	| 'mt'
	| 'nb'
	| 'nl'
	| 'pl'
	| 'pt'
	| 'ro'
	| 'sk'
	| 'sl'
	| 'sv'
	| 'tl'
	| 'ur';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
	'ar', 'bg', 'bn', 'cs', 'da', 'de', 'el', 'en', 'es', 'et',
	'fa', 'fi', 'fr', 'ga', 'hi', 'hr', 'hu', 'is', 'it', 'lb',
	'lt', 'lv', 'mt', 'nb', 'nl', 'pl', 'pt', 'ro', 'sk', 'sl',
	'sv', 'tl', 'ur',
];

@Injectable({ providedIn: 'root' })
export class I18nService {
	private readonly storageKey = 'bluedop_language';
	private readonly preferencesStorageKey = 'bluedop_user_preferences';

	readonly currentLanguage = signal<SupportedLanguage>('en');

	constructor(private translate: TranslateService) {
		this.translate.addLangs(SUPPORTED_LANGUAGES);
		this.translate.setDefaultLang('en');
	}

	async init(): Promise<void> {
		const initial = this.resolveInitialLanguage();
		await this.setLanguage(initial);
	}

	async setLanguage(languageCode: string): Promise<void> {
		const normalized = this.normalizeLanguage(languageCode);
		await firstValueFrom(this.translate.use(normalized));
		this.currentLanguage.set(normalized);
		localStorage.setItem(this.storageKey, normalized);
		document.documentElement.lang = normalized;
	}

	/**
	 * Applies language from API preferences response and caches to localStorage.
	 * Call this after fetching user preferences from the API.
	 */
	async applyFromPreferences(preferences: any): Promise<void> {
		const pref = preferences?.data ?? preferences;
		const lang = pref?.language;
		if (lang) {
			const normalized = this.normalizeLanguage(lang);
			await this.setLanguage(normalized);
		}

		// Cache full preferences for offline/next-load use and notify listeners.
		try {
			const existing = JSON.parse(localStorage.getItem(this.preferencesStorageKey) || '{}');
			const merged = {
				...existing,
				date_format: pref?.date_format ?? pref?.dateFormat ?? existing?.date_format,
				language: lang ?? existing?.language,
				timezone: pref?.timezone ?? pref?.time_zone ?? existing?.timezone,
			};

			localStorage.setItem(this.preferencesStorageKey, JSON.stringify(merged));
			window.dispatchEvent(new CustomEvent('bluedop-preferences-updated', { detail: merged }));
		} catch {
			// ignore storage errors
		}
	}

	t(key: string, params?: Record<string, unknown>): string {
		return this.translate.instant(key, params);
	}

	private resolveInitialLanguage(): SupportedLanguage {
		const preferenceLanguage = this.getStoredPreferenceLanguage();
		if (preferenceLanguage) {
			return this.normalizeLanguage(preferenceLanguage);
		}

		const stored = localStorage.getItem(this.storageKey);
		if (stored) {
			return this.normalizeLanguage(stored);
		}

		return this.normalizeLanguage(navigator.language || 'en');
	}

	private getStoredPreferenceLanguage(): string | null {
		try {
			const raw = localStorage.getItem(this.preferencesStorageKey);
			if (!raw) {
				return null;
			}

			const parsed = JSON.parse(raw);
			const lang = parsed?.language;
			return typeof lang === 'string' ? lang : null;
		} catch {
			return null;
		}
	}

	private normalizeLanguage(languageCode: string): SupportedLanguage {
		const code = (languageCode || '').trim().toLowerCase().replace('_', '-');

		const match = SUPPORTED_LANGUAGES.find((lang) => code === lang || code.startsWith(lang));
		return match ?? 'en';
	}
}

